// @flow
import Header from "./Header";
import BackButton from "../GUI_COMMON/COMPONENTS/BackButton";
import Popup from "../GUI_COMMON/COMPONENTS/Popup";
import ErrorPopup from "../GUI_COMMON/COMPONENTS/ErrorPopup";
import React, {useContext, useEffect, useState} from "react";
import ArchiverClientApi from "../API/archiverClientApi";
import {StoreContext} from "../GUI_COMMON/SUPPORT/store";
import {Redirect, useParams} from "react-router-dom";
import LIUtils from "../GUI_COMMON/SUPPORT/LIUtils";
import {useCookies} from "react-cookie";
import LoadingSpinner from "../GUI_COMMON/COMPONENTS/LoadingSpinner";
import ArchiverGuiUtils from "../SUPPORT/ArchiverGuiUtils";
import BTOOrderForm from "./BTOOrderForm";
import LIBtoCache from "../SUPPORT/LIBtoCache";
import {toast, Toaster} from "react-hot-toast";
import type MagazineV2 from "../GUI_COMMON/SUPPORT/MagazineV2";
import {useHistory} from "react-router";
import {boxIsGhostBox} from "./BTOPickSheetPage";
import LoadingOverlay from "../GUI_COMMON/COMPONENTS/LoadingOverlay";
import ReactDOMServer from "react-dom/server";
import ArchiverUtils from "../SUPPORT/ArchiverUtils";
import ExternalLinkIndicator from "./ExternalLinkIndicator";
import {SampleBadgePicked} from "./SampleBadgePicked";


// A BTOBox as from the server
export type BTOBox = {
    id: number,
    barcode: string,
    foreignReferenceName: string,
    holdsMagType: number,
    storageLocationId: number,
    dateModified: Date,
    quantity: number,
    parentOrderId: number,
    pendingOnOrderId: number,
    dateAdded: Date,
    datePickCompleted: Date,
    pickedBy: string,
};

export type BTOMagazineItem = MagazineV2 & {
    datePickCompleted: Date | null
}

// A BTOBoxAndMagsItem as from the server
export type BTOBoxAndMagsItem = {
    box: BTOBox,
    childMags: BTOMagazineItem[],
};

// our own internal BTOInfo object
export type BTOInfoItem = {
    saved: true,
    orderId: "",
    createdOn: Date,
    createdByName: "",
    createdById: -1,

    requestedByName: "",
    requestedById: -1,

    destLocation: -1,
    destDepartment: -1,

    notes: "",
    boxesAndChildMags: BTOBoxAndMagsItem[]
}

const BTOAddUpdatePage = () => {
    
    const initialBTOInfo:BTOInfoItem = {
        saved: true,
        orderId: "",
        createdOn: new Date(),
        createdByName: "",
        createdById: -1,
        
        requestedByName: "",
        requestedById: -1,

        destLocation: -1,
        destDepartment: -1,
        
        notes: "",
        boxesAndChildMags: []
    };
    
    const [globalState] = useContext(StoreContext);
    const [infoGoodnessList, setInfoGoodnessList] = useState({});
    const [btoInfo, setBtoInfo] = useState(initialBTOInfo);
    const [formIsValid, setFormIsValid] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [isLoadingBoxRange, setIsLoadingBoxRange] = useState(false);
    const [isUnPickingBoxBarcode, setIsUnPickingBoxBarcode] = useState(null);
    const [redirectToReplace, setRedirectToReplace] = useState();
    const [redirectToPush, setRedirectToPush] = useState();
    const [orderStatus, setOrderStatus] = useState({});
    const [isSaving, setIsSaving] = useState(false);
    const [checkForCompleted, setCheckForCompleted] = useState(false);
    const [savedFlag, setSavedFlag]=useState(true);     
    const [isInitializing, setIsInitializing] = useState(true);
    const [newlyAddedBoxIds, setNewlyAddedBoxIds] = useState(new Set());
    
    let {orderId} = useParams();
    orderId = Number(orderId??-1);
    
    const history = useHistory();
    const [cookies] = useCookies();
    
    useEffect( () => {
        if (orderId) {
            console.debug(`btoAddUpdate: [2] orderId: ${orderId}`);
            if (Number(orderId) === -1) { // new order...level needs to match.
                if (!LIUtils.UserHasLevel(cookies, "STORAGE_ADMIN")) {
                    Popup.show("", "<p>You are not a STORAGE ADMIN type user.</p>" +
                        "Talk to your Archiver Administrator if you wish to change this.", () => {
                        setRedirectToPush("/dashboard");
                    });
                }
            }
            
            // try to get it from the cache FIRST
            LIBtoCache.getBTOOrderIdAndSaveStatus().then(( {storedOrderId,isSaved}) => {
                if (storedOrderId !== -1 && storedOrderId === orderId && isSaved===false) { // load cached version
                    console.debug("btoAddUpdate: [0] order matches stored...copying info over");
                    (async () => { 
                        await readBTOInfoFromStorage();
                        console.debug(">>> btoAddUpdate: [1] readBTOInfoFromStorage done");
                        setIsInitializing(false);
                        setCheckForCompleted(true);
                    })(); // IIFE
                } else if (orderId === "" || orderId === -1) {
                    (async () => { 
                        await handleClearForm();
                        setIsInitializing(false);
                    })();  // IIFE
                } else {
                    setIsLoading(true);
                    setIsInitializing(true);
                    ArchiverClientApi.getBTOGivenId(orderId)
                    .then(({order, boxesAndChildMags}:{order: any, boxesAndChildMags: BTOBoxAndMagsItem[]}) => {
                        (async () => { 
                            await setupFormFieldData(order, boxesAndChildMags);
                            updateSavedFlagTo(true);
                            setIsLoading(false); // not dirty currently
                            setIsInitializing(false);
                            setCheckForCompleted(true);
                        })();  // IIFE
                    })
                    .catch(responseError => {
                        ArchiverGuiUtils.ShowErrorIfNotUnauthourized(responseError, () => {
                            setRedirectToPush("/btos");
                        });
                        setIsLoading(false);
                        setIsInitializing(false);
                        setCheckForCompleted(true);
                    })
                }
            });
        }
    },[cookies,orderId]);  // eslint-disable-line react-hooks/exhaustive-deps
    
    useEffect( () => {
        checkForValid();
    },[btoInfo]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect( () => {
        if(btoInfo && checkForCompleted) {
            console.debug("checking for BTO is completed...");
            if (btoInfo.boxesAndChildMags?.length > 0 && !shouldFormBeReadOnly()) {
                checkForOnlyPickedMagsAndShowPopup(btoInfo.boxesAndChildMags);
            }
            setCheckForCompleted(false); // reset
        }
    },[checkForCompleted]);  // eslint-disable-line react-hooks/exhaustive-deps
    
    useEffect( () => {
        
        console.debug("btoAddUpdate: [3] globalState.clientInfo: " + JSON.stringify(globalState.clientInfo));
        
        const curInfo = {...btoInfo};
        
        // always refresh this to be current
        curInfo.createdByName = globalState.clientInfo.fullName;
        curInfo.createdById = globalState.clientInfo.userId;

        setBtoInfo(curInfo);
        
        // the following causes load problems.
        // (async () => {
        //     await updateOurBTOInfo(curInfo,isInitializing,false);
        // })(); // IIFE
        
    },[globalState.clientInfo, isInitializing]); // eslint-disable-line react-hooks/exhaustive-deps

    function shouldFormBeReadOnly() {
        if(!LIUtils.UserHasLevel(cookies,"STORAGE_ADMIN")) { // can't edit - ever
            return true;
        }
        return orderStatus.isPickedCompleted || orderStatus.isMoved;
    }
    
    function showClearButtonIfRequired() {
        if(orderId!==-1) {
            return null;
        }
        return(
            <button className="li-button li-bg-success li-fg-white align-self-center mr-4" onClick={handleClearForm}>
                Clear
            </button>
        );
    }

    /**
     * reorder the information coming from the server into something that
     * our form understands, so we can reuse the form to display the details.
     *
     * @param order
     * @param boxesAndChildMags
     */
    async function setupFormFieldData(order, boxesAndChildMags:BTOBoxAndMagsItem[]) {

        const formFormattedInfo = initialBTOInfo;
        formFormattedInfo.orderId = orderId ? Number(order.id) : null;
        formFormattedInfo.createdOn = LIUtils.dateFromJsonUtcDateStr(order.dateStarted);
        formFormattedInfo.dateShipped = LIUtils.dateFromJsonUtcDateStr(order.dateShipped);
        formFormattedInfo.datePickedCompleted = LIUtils.dateFromJsonUtcDateStr(order.datePickCompleted);
        formFormattedInfo.createdById = order.creator.id;
        formFormattedInfo.createdByName = order.creator.fullName;
        formFormattedInfo.requestedByName = order.requester.fullName;
        formFormattedInfo.requestedById = order.requester.id;
        formFormattedInfo.destLocation = order.destinationLocation?.id;
        formFormattedInfo.destDepartment = order.destinationDepartment?.id;
        formFormattedInfo.notes = order.notes;
        formFormattedInfo.boxesAndChildMags = boxesAndChildMags;
        formFormattedInfo.saved = true; // not updated
        
        // order is important here!
        setOrderStatus({
            isPickedCompleted: !!order.datePickCompleted,
            cancelTimeIfPicked: LIUtils.dateFromJsonUtcDateStr(order.cancelTimeIfPicked),
            isMoved: !!order.dateMoved
        })
        await updateOurBTOInfo(formFormattedInfo, false);
    }
    
    async function readBTOInfoFromStorage() {
        console.debug("reading BTOInfoFromStorage");
        let curInfo = await LIBtoCache.getBTOInfo();
        
        // always refresh this
        curInfo.createdByName = globalState.clientInfo.fullName;
        curInfo.createdById = globalState.clientInfo.userId;
        curInfo.createdOn = curInfo.createdOn?new Date(curInfo.createdOn):new Date();
        setBtoInfo(curInfo);
        
        updateSavedFlagTo(curInfo.saved);
    }
    
    function updateSavedFlagTo(updatedFlag) {
        console.debug(`updating saved flag to ${updatedFlag}`);
        setSavedFlag(updatedFlag);
    }
    
    async function handleAddBoxes(boxRange) {
        console.debug(`handleAddBoxes: range is '${boxRange}'...`);
        if(!LIUtils.existsAndNotEmpty(boxRange)) {
            return;
        }
        if (ArchiverUtils.CheckBoxRangeForWarning(boxRange)) { // there are ALOT of boxes here -- really?
            Popup.showConfirm("Add Boxes?",
                "<p>There are potentially a lot of boxes in the range '" + boxRange + "'.<br/>Are you sure you want to add all of them?</p>",
                "Yes, add them!",null, () => {
                    doRealHandleAddBoxes(boxRange);
                }, null, null,"No, cancel");
        }
        else { // just do it.
            await doRealHandleAddBoxes(boxRange);
        }
    }
    
    async function doRealHandleAddBoxes(boxRange) {
        setIsLoadingBoxRange(true);
        try {
            const boxesAndChildMags: BTOBoxAndMagsItem[] = await ArchiverClientApi.getBoxRangeForBTO(boxRange, orderId);
            if (boxesAndChildMags.length === 0) {
                //toast.error("No Boxes Found", "No boxes found for box range " + boxRange);
                Popup.showInfoWithButtonTimed("No boxes found for box range " + boxRange,
                    ()=>{},true, 500,true, 2000);
            } else { // we're dirty
                await LIBtoCache.addBoxesToBTOList(boxesAndChildMags);
                setBtoInfo(await LIBtoCache.getBTOInfo()); // inefficient
                updateSavedFlagTo(false);
                
                // Track newly added box IDs for visual feedback
                const newBoxIds = boxesAndChildMags.map((item) => item.box.id);
                setNewlyAddedBoxIds(new Set(newBoxIds));
                
                // Clear the newly added box IDs after animation duration (1.5 seconds for flash animation)
                setTimeout(() => {
                    setNewlyAddedBoxIds(new Set());
                }, 1600);
            }
        }
        catch(responseError) {
            ArchiverGuiUtils.ShowErrorIfNotUnauthourized(responseError, () => {
            });
        } 
        finally {
            setIsLoadingBoxRange(false);
        }
    }
    
    async function handleClearForm() {
        console.debug("clearing form...");

        // clear the box range - HACK
        const el = document.getElementById("addBoxesTB");
        if(el) el.value="";
        
        // reset the created by info to the current user
        initialBTOInfo["createdByName"]=globalState.clientInfo.fullName;
        initialBTOInfo["createdById"]=globalState.clientInfo.userId;
        await LIBtoCache.clearBTOInfo(initialBTOInfo);
        
        // not saved and clean
        await updateOurBTOInfo({...initialBTOInfo}, true,true);
    }
    
    async function handleInfoChanged(event) {
        console.debug(`handleInfoChanged for ${event.target?.name}`);
        let updateInfo = {...btoInfo};
        const thing = event.target.name;

        switch (thing) {
            case "bto-destLocation":
                const locId = Number(event.target.value)
                updateInfo["destLocation"] = locId;
                updateInfo["destDepartment"] = -1;
                break;
            case "bto-destDepartment":
                updateInfo["destDepartment"] = Number(event.target.value);
                break;
            case "requestedBy":
                const contactItem = event.target["contact"];  // augmented
                updateInfo["requestedById"] = Number(contactItem.id);
                updateInfo["requestedByName"] = contactItem.fullName;
                break;
            default:
                updateInfo[thing] = event.target.value;
                break;
        }
        
        await updateOurBTOInfo(updateInfo, !isInitializing, false);
    }
    
    async function updateOurBTOInfo(updateInfo, updateSavedFlag=true, updateSavedFlagTo=false) {

        // these 2 need to stay in sync
        if (updateSavedFlag) {
            updateInfo["saved"] = updateSavedFlagTo;
            setSavedFlag(updateSavedFlagTo);
        }

        await LIBtoCache.setBTOInfo(updateInfo);
        setBtoInfo(updateInfo);
    }
    
    function checkForValid() {
        const goodNessList = {...infoGoodnessList};
        let isValid=0;
        if(btoInfo.requestedById>0 && btoInfo.requestedByName!=="") { // mark green
            isValid|=1;
            goodNessList["requestedBy"]="good";
        }
        else {
            goodNessList["requestedBy"]="bad";
        }
        if(LIUtils.existsAndNotEmpty(btoInfo.createdByName)) { // mark green
            isValid|=2;
        }
        if(hasDestination()) { // mark green
            isValid|= 4;
            goodNessList["destLocation"]="good";
        }
        else {
            goodNessList["destLocation"]="bad";
        }
        
        setInfoGoodnessList(goodNessList);
        setFormIsValid(isValid >= 7); // all good if all flags=1
    }
    
    function hasDestination() {
        return btoInfo.destLocation>0;
    }
    
    function handleFormSubmit(event) {
        event.stopPropagation(); // let the validation checks run
        event.preventDefault();

        const form = document.getElementById('btoOrderForm');
        if (form.checkValidity() === false) {
            return;
        }
        doFormSave();
    }
    
    function doFormSave() {
        setIsSaving(true);
        if(isNewOrder()) { // ADD NEW ORDER
            ArchiverClientApi.sendAddBTO(btoInfo)
            .then(({order, boxesAndChildMags}:{order: any, boxesAndChildMags: BTOBoxAndMagsItem[]}) => {
                (async () => { // refresh our form data to ensure it matches
                    await setupFormFieldData(order, boxesAndChildMags);
                    updateSavedFlagTo(true);
                })();  // IIFE
                Popup.show("Order Created", 
                    `Box Transfer Order '<b>${LIUtils.prettyOrderNumber(order.id)}</b>' has been created`, 
                    async () => {
                    updateSavedFlagTo(true);
                    await clearCacheAndReloadOrder(order.id);
                });
            })
            .catch( errorResponse => ErrorPopup.showError("Save Failed",errorResponse.error))
            .finally(() => {
                setIsSaving(false);
            });
        }
        else { // UPDATE EXISTING ORDER
            ArchiverClientApi.sendUpdateBTO(orderId,btoInfo)
            .then( ({order, boxesAndChildMags, message}:{order: any, boxesAndChildMags: BTOBoxAndMagsItem[], message: string}) => {
                (async () => {
                    await setupFormFieldData(order, boxesAndChildMags);
                    updateSavedFlagTo(true);
                })();  // IIFE
                
                const extraInfo = message ? '<H3><span class="fa fa-exclamation-triangle li-fg-danger">&nbsp;</span>&nbsp;HOWEVER</H3><p>' + 
                    LIUtils.replaceNewLinesWithBreaks(message)+"</p>" : "";
                Popup.show("Saved", 
                    `<p>Box Transfer Order <b>'${LIUtils.prettyOrderNumber(order.id)}'</b> has been updated.<p>${extraInfo}`, 
                    async () => {
                    setCheckForCompleted(true);
                });
                
            })
            .catch( errorResponse => ErrorPopup.showError("Save Failed",errorResponse.error))
            .finally(() => {
                setIsSaving(false);
            });
        }
    }
    
    function handleBoxClicked(boxWithMags:BTOBoxAndMagsItem) {
        const box = boxWithMags.box;
        console.debug(`handleBoxClicked: ${box.id}...`);
        const mags = boxWithMags.childMags;
        
        let warningPrefix = "";
        let ghostPostFix = "";
        let boxLink = null;
        if (boxIsGhostBox(box)) {
            warningPrefix = `<span class="fa fa-exclamation-triangle li-fg-danger">&nbsp;</span>`;
            ghostPostFix = " (these are not in boxes)";  
            boxLink = box.barcode;
        }
        else {
            boxLink = ReactDOMServer.renderToString(<span className=""><a href={`/box_history/${box.barcode}`}
                                                       target="_box_details">{box.barcode}
                                                     <ExternalLinkIndicator small/></a>
                                                    </span>);
        }

        let magsStr = mags.length === 0 ? "No magazines found" : "";
        mags.forEach((mag) => {
            const pickedBadge = mag.datePickCompleted?
                    <SampleBadgePicked/>:null;
            const magLineObj = 
                <li>
                    <a href={"/mag_details/"+mag.barcode} target="_mag_details" className="mr-2">{mag.barcode} <ExternalLinkIndicator small/></a>
                    {pickedBadge}
                    <i className="fa fa-caret-right mx-2 li-fg-primary"></i>
                    <span className="li-fg-black li-font-small text-right">
                            {ArchiverGuiUtils.GetShortLocationStringGivenMagStorageLocation(mag.storageLocation)}
                    </span>
                </li>;
            magsStr += ReactDOMServer.renderToString(magLineObj,true);
        });

        Popup.showInfoWithButton(`<h3>${warningPrefix}Box: ${boxLink}</h3><h5 class="text-left">Magazines${ghostPostFix}</h5>
                                            <ul class="text-left">${magsStr}</ul>`, null,true, 600);
    }

    function handleBoxPendingOrderClicked(btoOrderId:number) {
        window.open(`/btos/details/${btoOrderId}`, "_pending_bto");
    }
    
    async function handleBoxBadgeClicked(box:BTOBox, badgeText:string) {
        if(badgeText==="+ Ref") { // magic string here
            Popup.showPrompt(`Add Reference for ${box.barcode}?`,box.foreignReferenceName??"",
                "Set Reference",() => {},
                async (refText) => {
                await doAddReferenceToBoxAtServer(orderId, box, refText);
            },1, "");
        }
    }
    
    async function doAddReferenceToBoxAtServer(orderId:number, box:BTOBox, refText:string) {
     
        // we only send the ref to the server if the form has been saved -- otherwise we'll just send it up with
        // the next save
        if (box.foreignReferenceName===refText) {
            return; // don't bother -- same ref
        }
        
        try {
            const message:string = await ArchiverClientApi.sendAddReferenceToSingleBoxRequest(orderId, box.id, refText);
            if (message==="") { // all good...update the cache
                await LIBtoCache.addReferenceToBox(box, refText);
                await readBTOInfoFromStorage(); // ### inefficient.
            }
            else {
                ErrorPopup.showError("Add Reference Failed",message);
            }
        }
        catch( responseError) {
            ArchiverGuiUtils.ShowErrorIfNotUnauthourized(responseError);
        }
    }
    
    function boxHasBeenPicked(box:BTOBox) {
        return box.parentOrderId===Number(orderId) && box.datePickCompleted!=null;
    }
    
    function magsInTheBoxHaveBeenPicked(mags:BTOMagazineItem[]) {
        return mags.some((mag) => mag.datePickCompleted!=null);
    }
    
    async function handleRemoveBox(boxAndChildMags:BTOBoxAndMagsItem) {
        if(boxHasBeenPicked(boxAndChildMags.box)) {
            // this BOX has ALREADY BEEN PICKED
            Popup.showConfirm("Box Already Picked",
                "<p><i class='fa fa-exclamation-triangle li-fg-danger'>&nbsp;</i>" +
                "Box '"+boxAndChildMags.box.barcode+"' has already been picked in this order.</p><p>If you remove this box, " +
                "it will first be unpicked, and <b>YOU</b> will need to be sure that it goes back to where it came from.</p>" +
                "<p>You can then use the <span class=\"li-fg-danger fa fa-times-circle fa-lg li-font\">&nbsp;</span>button once again to remove the box.</p>" +
                "<p>Are you sure you want to unpick this box and its magazines?</p>",
                "Yes, Unpick These!",null,
                () => doUnpickBoxAtServer(orderId,boxAndChildMags),null,null,"No, Don't Unpick");
            return;
        }
        if(magsInTheBoxHaveBeenPicked(boxAndChildMags.childMags)) {
            // mags in this box have already been picked
            Popup.showConfirm("Box Has Picked Magazines",
                "<h4><i class='fa fa-exclamation-triangle li-fg-danger mr-2'>&nbsp;</i>At least one magazine in this box has already been picked.</h4><p>If you remove this box, " +
                "the magazines will first be unpicked, and <b>YOU</b> will need to be sure that the mags go back to where they came from.</p>" +
                "<p>You can then use the <span class=\"li-fg-danger fa fa-times-circle fa-lg li-font\">&nbsp;</span>button once again to remove the box.</p>" +
                "<p>Are you sure you want to unpick this box and its magazines?</p>",
                "Yes, Unpick These!",null,
                () => doUnpickBoxAtServer(orderId,boxAndChildMags), null,null,"No, Don't Unpick");
            return;
        }
        
        // remove the box since it has not been picked yet
        await LIBtoCache.removeBoxesFromBTOList([boxAndChildMags]);
        await readBTOInfoFromStorage(); // ### inefficient.
    }

    async function doUnpickBoxAtServer(currentOrderId:number, boxAndChildMags:BTOBoxAndMagsItem) {
        const box = boxAndChildMags.box;
        try {
            setIsUnPickingBoxBarcode(box.barcode);
            const childMagIds = boxAndChildMags.childMags.map((mag) => mag.id);
            const {numBoxesUpdated, numMagsUpdated}:{numBoxesUpdated: number, numMagsUpdated: number} = 
                        await ArchiverClientApi.sendUnpickBoxAndMagsRequest(currentOrderId,box.id, childMagIds);
            if (numBoxesUpdated > 0 || numMagsUpdated > 0) { // we did something there...update the cache
                await LIBtoCache.unpickBoxAndChildMagsFromBTO(boxAndChildMags, false);
                await readBTOInfoFromStorage(); // ### inefficient.
                if(numBoxesUpdated>0) {
                    toast.success("Unpicked "+numBoxesUpdated+" boxes.");
                }
                if(numMagsUpdated>0) {
                    toast.success("Unpicked "+numMagsUpdated+" magazines.");
                }
            }
            else {
                toast.error("Nothing to unpick.");
            }
        }
        catch( responseError) {
            ArchiverGuiUtils.ShowErrorIfNotUnauthourized(responseError);
        } 
        finally {
            setIsUnPickingBoxBarcode(null);
        }
    }
    
    function generatePageTitle() {
        if (orderId === -1) {
            return (<h1 className="d-inline-block align-middle">New Box Transfer Order</h1>);
        }
        return(
            <div className="align-self-center">
                <h1 className="d-inline-block align-middle m-0">Box Transfer Order</h1>
                <div className="text-right li-font-medium">
                    <i>Order #:</i> {LIUtils.prettyOrderNumber(btoInfo?.orderId)}
                </div>
            </div>
        );
    }
    
    function isNewOrder():boolean {
        return !orderId || orderId==="" || Number(orderId)===-1;
    }
    
    function showPickAndPrintAndCompletedIfRequired() {
        if (isNewOrder()) { // don't show for completed or new orders
            return null;
        }
        
        let pickCompletedExtra = null;
        if (orderStatus.isPickedCompleted) {
            let cancelExtra = "";
            if(!orderStatus.isMoved && orderStatus.cancelTimeIfPicked && LIUtils.isDateInFuture(orderStatus.cancelTimeIfPicked)) {
                cancelExtra = <span className="d-block mt-1 li-font-small ml-2">
                    [ it can be cancelled until {LIUtils.dateTimeToPretty(orderStatus.cancelTimeIfPicked)} ]</span>;
            }
            pickCompletedExtra = <div className="badge badge-success li-font-medium col-10">
                                    <span className="fa fa-check align-self-center mt-1"></span>&nbsp;This order has been fully picked
                                    {cancelExtra}
                                </div>;
        }
        
        if(shouldFormBeReadOnly()) { // dump print button only
            return (
                <div className={"row "+(pickCompletedExtra?"justify-content-between":"justify-content-end")}>
                    {pickCompletedExtra}
                    <button id="printSheetBtn" className="li-button li-primary" onClick={handlePrintPage}>Print</button>
                </div>
            );
        }
        
        return(
            <div className="text-right mt-3">
                <button id="genPickSheetBtn" className="li-button li-primary mr-5" 
                        disabled={!savedFlag || hasNoBoxesOnOrder()}
                        onClick={handleGeneratePickSheet}>Generate Pick sheet</button>
                <button id="printSheetBtn" className="li-button li-primary" onClick={handlePrintPage}>Print</button>
            </div>
        );
    }
    
    function hasNoBoxesOnOrder() {
        return btoInfo?.boxesAndChildMags?.length===0;
    }

    function handleGeneratePickSheet() {
        
        if(LIUtils.UserHasLevel(cookies,"PICKER") || LIUtils.UserHasLevel(cookies,"STORAGE_ADMIN")) {
            if(!savedFlag) {
                Popup.showConfirm("","Changes have not been saved.  Really leave?","Leave",null,() => {
                    setRedirectToPush("/btos/pick_sheet?" + orderId);
                },
                    null, null, "Stay");
                return;
            }
            setRedirectToPush("/btos/pick_sheet?" + orderId);  
        }
        else {
            Popup.show("", "<p>You are not a PICKER or STORAGE ADMIN type user.</p>" +
                "Talk to your Archiver Administrator if you wish to change this.");
        }
    }
    
    function handlePrintPage() {
        window.print();
    }
    
    async function handleBackClicked() {
        if(!savedFlag) {
            Popup.showConfirm("","Changes have not been saved.  Really leave?","Leave",null,async () => {
                    await clearCacheAndGoBack();
                },
                null, null, "Stay");
        }
        else {
            await clearCacheAndGoBack();
        }
    }

    async function clearCacheAndGoBack() {
        await LIBtoCache.clearBTOInfo(); // wipe out any edits
        //setRedirectToReplace("/btos");
        history.go(-1);
    }
    
    async function clearCacheAndReloadOrder(orderId) {
        
        // redirect to the new order
        await LIBtoCache.clearBTOInfo(); // wipe out any edits
        history.replace("/btos/details/" + orderId); 
        history.go(0); // reload
    }
    
    function checkForOnlyPickedMagsAndShowPopup(magsAndBoxes:{box:BTOBox,childMags:any[]}[]) {
        // get the mags that are either not picked yet, or newly added
        const unpickedBoxes = magsAndBoxes.filter(b => 
                                    b.box.datePickCompleted===null || b.box.datePickCompleted===undefined);
        if(unpickedBoxes.length===0 && magsAndBoxes.length>0) { // only picked remain
            console.debug(" order is complete -- only picked items remain");
            Popup.showConfirm("Complete the Order?","<p>All boxes on this order have been picked.</p><p>Do you want to move it to the 'Completed' status?</p>",
            "Move to Completed",null,() => {
                    ArchiverClientApi.markBTOAsPicked(orderId)
                    .then( async ({order,message}:{order: any, message: string}) => {
                        if(order.isPickCompleted) {
                            await LIBtoCache.clearBTOInfo(); // ensure our long term store is clear
                            setRedirectToReplace("/btos");
                        }
                        else {
                            await LIBtoCache.setBTOInfo(order);
                            ErrorPopup.showError("Order Not Completed", message, async () => {
                                await forceCacheClearReload();
                            });
                        }
                    })
                    .catch(responseError => ArchiverGuiUtils.ShowErrorIfNotUnauthourized(responseError))
                    .finally(() => {
                        setIsSaving(false);
                    });
                }, null,null,"No");
        }
    }

    async function handleAddNoteClicked() {
        Popup.showPrompt("Add Note","","Save",() => {},async (noteText) => {
            await doAddNoteToOrderAtServer(btoInfo.orderId, noteText);
        },1);
    }

    async function doAddNoteToOrderAtServer(orderId, noteText) {
        try {
            const updatedNote:string = await ArchiverClientApi.addNoteToBTOAtServer(orderId, noteText);
            await LIBtoCache.updateBTONote(updatedNote);
            await readBTOInfoFromStorage(); // ### inefficient
        } catch (responseError) {
            ErrorPopup.showError("Error", responseError);
        }
    }
    
    if(redirectToPush) {
        return <Redirect push to={redirectToPush}/>;
    }
    if(redirectToReplace) {
        return <Redirect to={redirectToReplace}/>;
    }

    async function forceCacheClearReload() {
        await LIBtoCache.clearBTOInfo(); // wipe out any edits
        history.go(0);
    }

    return(
        <div className="container">
            <Header title={"Box Transfer Order - "+orderId}/>
            <div className="row li-header-row m-0 p-0 justify-content-between">
                <div className="align-self-center">
                    <BackButton onClick={handleBackClicked}/>
                    <div className="d-inline-block align-middle">{generatePageTitle()}</div>
                    <div className="d-inline-block ml-5">{isLoading && <LoadingSpinner medium/>}</div>
                </div>
                {showClearButtonIfRequired()}
                <button className="btn ml-auto mr-3 align-self-center" onClick={forceCacheClearReload}
                            data-toggle="tooltip" data-placement="bottom" title="Reload"><i className="fa fa-redo"/></button>
            </div>

            <div style={{minHeight:"800px"}}>
                {isInitializing && <div className="text-center">...Loading Box Transfer Order...</div>}
                {!isInitializing && <>
                {showPickAndPrintAndCompletedIfRequired()}
    
                <BTOOrderForm onSubmit={handleFormSubmit} btoInfo={btoInfo}
                                    onAddBoxes={handleAddBoxes} 
                                    onChange={handleInfoChanged} 
                                    goodNess={infoGoodnessList}
                                    onBoxClicked={handleBoxClicked}
                                    onBoxOrderClicked={handleBoxPendingOrderClicked}
                                    onBoxBadgeClicked={handleBoxBadgeClicked}
                                    onRemoveBox={handleRemoveBox}
                                    formIsValid={formIsValid}
                                    formIsReadOnly={shouldFormBeReadOnly()} 
                                    isSaving={isSaving} 
                                    isDirty={!savedFlag} 
                                    isLoadingBoxes={isLoadingBoxRange}
                                    onAddNoteClicked={handleAddNoteClicked}
                                    newlyAddedBoxIds={newlyAddedBoxIds}/>
                </>
            }
            </div>
            <Toaster position="top-center" reverseOrder={false} gutter={8}/>
            <LoadingOverlay show={isUnPickingBoxBarcode!==null} message={"Unpicking Box '"+isUnPickingBoxBarcode+"'..."} />
        </div>  
    );
    
}

export default BTOAddUpdatePage;